<template>
  <div id="app">
    <!-- 顶部导航 - 仅在首页显示 -->
    <HeaderNav 
      v-if="currentPage === 'home'"
      :locale="currentLocale"
      @go-home="goToHome"
      @change-locale="changeLocale"
    />
    
    <!-- 首页 -->
    <HomePage 
      v-if="currentPage === 'home'" 
      ref="homePage"
      :locale="currentLocale"
      @select-product="goToProductDetail"
    />
    
    <!-- 产品详情页 -->
    <ProductDetail 
      v-else-if="currentPage === 'product'"
      :product-id="selectedProductId"
      :locale="currentLocale"
      @back="goToHome"
      @change-locale="changeLocale"
    />
    
    <FooterSection 
      v-if="currentPage === 'home'" 
      :locale="currentLocale"
    />
  </div>
</template>

<script>
import HeaderNav from './components/HeaderNav.vue'
import HomePage from './components/HomePage.vue'
import ProductDetail from './components/ProductDetail.vue'
import FooterSection from './components/FooterSection.vue'
import { getCurrentLocale, setLocale } from './i18n/index.js'

export default {
  name: 'App',
  components: {
    HeaderNav,
    HomePage,
    ProductDetail,
    FooterSection
  },
  data() {
    return {
      currentPage: 'home',
      selectedProductId: null,
      currentLocale: getCurrentLocale(),
      savedScrollPosition: 0
    }
  },
  created() {
    this.checkUrlParams()
    window.addEventListener('popstate', this.handlePopState)
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.handlePopState)
  },
  methods: {
    checkUrlParams() {
      const urlParams = new URLSearchParams(window.location.search)
      const productId = urlParams.get('product')
      
      if (productId) {
        this.selectedProductId = Number(productId)
        this.currentPage = 'product'
      } else {
        this.currentPage = 'home'
        this.selectedProductId = null
        if (window.location.search) {
          window.history.replaceState({ page: 'home' }, '', window.location.pathname)
        }
      }
    },
    
    handlePopState(event) {
      if (event.state) {
        if (event.state.page === 'home') {
          this.currentPage = 'home'
          this.selectedProductId = null
          // 恢复滚动位置
          this.$nextTick(() => {
            setTimeout(() => {
              window.scrollTo({ top: this.savedScrollPosition, behavior: 'auto' })
            }, 50)
          })
        } else if (event.state.page === 'product' && event.state.productId) {
          this.currentPage = 'product'
          this.selectedProductId = event.state.productId
          window.scrollTo({ top: 0, behavior: 'auto' })
        }
      } else {
        this.checkUrlParams()
      }
    },
    
    goToHome(sectionId = null) {
      this.currentPage = 'home'
      this.selectedProductId = null
      
      const newUrl = window.location.pathname
      window.history.pushState({ page: 'home' }, '', newUrl)
      
      if (sectionId) {
        this.$nextTick(() => {
          setTimeout(() => {
            const element = document.getElementById(sectionId)
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' })
            }
          }, 100)
        })
      } else {
        // 恢复滚动位置
        this.$nextTick(() => {
          setTimeout(() => {
            window.scrollTo({ top: this.savedScrollPosition, behavior: 'auto' })
          }, 50)
        })
      }
    },
    
    goToProductDetail(productId) {
      if (!productId) return
      
      // 保存当前滚动位置
      this.savedScrollPosition = window.scrollY || window.pageYOffset
      
      this.selectedProductId = Number(productId)
      this.currentPage = 'product'
      
      const newUrl = `${window.location.pathname}?product=${productId}`
      window.history.pushState(
        { page: 'product', productId: Number(productId) },
        '',
        newUrl
      )
      
      window.scrollTo({ top: 0, behavior: 'auto' })
    },
    
    changeLocale(locale) {
      this.currentLocale = locale
      setLocale(locale)
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  font-family: var(--font-family-base);
  font-size: var(--font-size-base);
  line-height: var(--line-height-normal);
  color: var(--color-text);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: var(--color-surface);
}

#app {
  font-family: var(--font-family-base);
  color: var(--color-text);
  line-height: var(--line-height-normal);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.container {
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--space-lg);
}

.section-title {
  text-align: center;
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-heading);
  margin-bottom: var(--space-2xl);
  position: relative;
  padding-bottom: var(--space-md);
  letter-spacing: var(--letter-spacing-tight);
  line-height: var(--line-height-tight);
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, var(--color-primary), var(--color-primary-light));
  border-radius: var(--radius-full);
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
  padding: var(--space-md) var(--space-2xl);
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-light) 100%);
  color: var(--color-surface);
  text-decoration: none;
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-base);
  letter-spacing: var(--letter-spacing-wide);
  transition: all var(--transition-base);
  border: none;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-primary);
  background: linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%);
}

.btn-primary:active {
  transform: translateY(0);
}

@media (max-width: 768px) {
  .container {
    padding: 0 var(--space-md);
  }

  .section-title {
    margin-bottom: var(--space-xl);
  }
}
</style>
